import * as React from "react"

import { cn } from "../lib/utils"

interface RadioProps extends Omit<React.ComponentProps<"input">, "type"> {
  /** Visible label rendered beside the control and wired up via htmlFor. */
  label?: React.ReactNode
  /** Secondary copy under the label, exposed as the input's description. */
  description?: React.ReactNode
}

/**
 * Native `<input type="radio">` with the DS treatment. Same rationale as
 * {@link Checkbox} — the app's radios are plain grouped inputs keyed by `name`.
 */
const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ className, label, description, id, ...props }, ref) => {
    const generatedId = React.useId()
    const inputId = id || generatedId
    const descriptionId = description ? `${inputId}-description` : undefined

    const input = (
      <input
        type="radio"
        id={inputId}
        ref={ref}
        aria-describedby={descriptionId}
        className={cn(
          // No `rounded` — the browser draws radios round; `rounded` would
          // square off the corners of the focus ring box.
          "h-4 w-4 border-neutral-300 text-primary-600 shadow-sm",
          "focus:ring-2 focus:ring-primary-500 focus:ring-offset-0",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      />
    )

    if (!label && !description) {
      return input
    }

    return (
      <div className="flex items-start gap-2">
        {input}
        <div className="grid gap-0.5 leading-none">
          {label && (
            <label
              htmlFor={inputId}
              className={cn(
                "text-sm font-medium text-neutral-700 select-none",
                props.disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {label}
            </label>
          )}
          {description && (
            <p id={descriptionId} className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
      </div>
    )
  }
)
Radio.displayName = "Radio"

interface RadioGroupProps extends Omit<React.ComponentProps<"div">, "role"> {
  /** Group label — becomes the radiogroup's accessible name. */
  label?: React.ReactNode
  /** Validation message; also flips the group to aria-invalid. */
  error?: string
  /** Lay the options out side by side instead of stacked. */
  orientation?: "vertical" | "horizontal"
}

/**
 * Accessible wrapper around a set of {@link Radio}s. Grouping is what makes a
 * radio set announce as "1 of 3" — a bare stack of inputs does not.
 */
const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    { className, label, error, orientation = "vertical", children, ...props },
    ref
  ) => {
    const generatedId = React.useId()
    const labelId = label ? `${generatedId}-label` : undefined
    const errorId = error ? `${generatedId}-error` : undefined

    return (
      <div className="w-full">
        {label && (
          <span
            id={labelId}
            className="mb-1 block text-sm font-medium text-neutral-700"
          >
            {label}
          </span>
        )}
        <div
          ref={ref}
          role="radiogroup"
          aria-labelledby={labelId}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          className={cn(
            "flex",
            orientation === "vertical" ? "flex-col gap-2" : "flex-row gap-4",
            className
          )}
          {...props}
        >
          {children}
        </div>
        {error && (
          <p id={errorId} className="mt-1 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    )
  }
)
RadioGroup.displayName = "RadioGroup"

export { Radio, RadioGroup }
export type { RadioProps, RadioGroupProps }
